/* eslint-disable no-console */
import { useCallback, useEffect, useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  BaseSignalRMessage,
  SignalRFilterKey,
  SignalRScheduleMessage,
} from '../../../hooks/use-signalr';
import { useAppContext } from '../../context/app-context';
import {
  GetScheduleDateTemplateItemsDocument,
  GetScheduleDateTemplateItemsQueryVariables,
  PushType,
  ScheduleDateTemplateItemDataFragment,
} from '../../types/api/graphql';
import { getDatesOverlap } from '../../utils/date.utils';
import { PracticeId } from '../types';

export function useScheduleDateTemplateItems(
  variables: GetScheduleDateTemplateItemsQueryVariables,
  disableSignalR?: boolean
) {
  const {
    data: scheduleDateTemplateItemsData,
    loading: scheduleDateTemplateItemsLoading,
    refetch,
  } = useCacheQuery(GetScheduleDateTemplateItemsDocument, {
    variables: {
      ...variables,
      healthProfessionalId:
        variables.healthProfessionalId === PracticeId
          ? null
          : variables.healthProfessionalId,
    },
    skip:
      !variables.healthProviderId || !variables.startDate || !variables.endDate,
  });

  const { signalRMessage, addFilter } = useAppContext();

  const filter = useCallback(
    (message: BaseSignalRMessage) => {
      if (disableSignalR) return false;

      if (
        [PushType.ScheduleUpdated, PushType.AgendaTemplateApplied].includes(
          message.type
        )
      ) {
        const { healthProfessionalId, healthProviderId, startDate, endDate } =
          message as SignalRScheduleMessage;

        const isHealthProfessionalMatch =
          healthProfessionalId === variables.healthProfessionalId ||
          variables.healthProfessionalId === PracticeId;
        const isHealthProviderMatch =
          healthProviderId === variables.healthProviderId;

        if (isHealthProfessionalMatch && isHealthProviderMatch) {
          if (
            getDatesOverlap(
              {
                start: new Date(variables.startDate),
                end: new Date(variables.endDate),
              },
              { start: new Date(startDate), end: new Date(endDate) }
            )
          ) {
            return true;
          }
        }
      }
      return false;
    },
    [
      disableSignalR,
      variables.healthProfessionalId,
      variables.healthProviderId,
      variables.startDate,
      variables.endDate,
    ]
  );

  useEffect(() => {
    if (disableSignalR === true) return;
    if (scheduleDateTemplateItemsLoading) return;
    if (variables.healthProviderId) {
      addFilter({ key: SignalRFilterKey.SCHEDULEDATE, filter });
    }
  }, [
    addFilter,
    disableSignalR,
    filter,
    scheduleDateTemplateItemsLoading,
    variables.healthProviderId,
  ]);

  useEffect(() => {
    if (signalRMessage && filter(signalRMessage)) {
      console.log(`[${SignalRFilterKey.SCHEDULEDATE}] refetch`);
      refetch();
    }
  }, [filter, refetch, signalRMessage]);

  const data = useMemo(
    () =>
      (scheduleDateTemplateItemsData?.healthProfessionalTemplateDays ?? []).map(
        option => option as ScheduleDateTemplateItemDataFragment
      ),

    [scheduleDateTemplateItemsData?.healthProfessionalTemplateDays]
  );

  return {
    data,
    loading: scheduleDateTemplateItemsLoading,
  };
}
